import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismIcon } from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import PowerCalendarMultiple from 'ember-power-calendar/components/power-calendar-multiple';
// eslint-disable-next-line import-x/no-named-as-default
import powerCalendarFormatDate from 'ember-power-calendar/helpers/power-calendar-format-date';
import { not, or } from 'ember-truth-helpers';
import type { PaAutopilotSettings } from 'frontend-utils/models/hotel';
import { MAX_ALLOWED_MONTHS_IN_FUTURE } from 'lumen/utils/constants/pricing-assistant-constants';
import { trackAutopilotDayToggle } from 'lumen/utils/pricing-assistant/track-autopilot-day-toggle';

const MAX_NUMBER_OF_MONTHS_SHOWN_AT_ONCE = 3;

interface SettingsAutopilotCalendarsExceptionDatesSignature {
  Args: {
    areAutopilotSettingsActionOrInputDisabled: boolean;
    isAutopilotEnabled: boolean;
    localSettingsCopy: PaAutopilotSettings;
    updateLocalSettingsCopy: (newSettings: PaAutopilotSettings) => void;
  };
}

export default class SettingsAutopilotCalendarsExceptionDates extends Component<SettingsAutopilotCalendarsExceptionDatesSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // First month shown out of allMAX_NUMBER_OF_MONTHS_SHOWN_AT_ONCE months shown
  @tracked firstCurrentMonthShown = this.today;

  get calendarMonthsShown(): Date[] {
    const monthsArray = [];
    let currentMonth = this.firstCurrentMonthShown;

    // Add months to array until we reach the max number of months to be shown at once
    while (
      monthsArray.length < MAX_NUMBER_OF_MONTHS_SHOWN_AT_ONCE &&
      currentMonth.isSameOrBefore(this.lastAllowedMonth, 'month')
    ) {
      monthsArray.push(currentMonth);
      currentMonth = currentMonth.add(1, 'month');
    }

    return monthsArray.map((month) => month.toDate());
  }

  get calendarsMinDate(): Date {
    // Power calendar disable the days before the minDate
    // In case autopilot is disabled or user doesn't have edit rights, we want all the days to be in disabled state, therefore, we set the minDate as the last allowed date
    if (this.args.areAutopilotSettingsActionOrInputDisabled) {
      // Adding 1 day to the last allowed month end date to make the minDate inclusive
      return this.lastAllowedMonthEndDate.add(1, 'day').toDate();
    }

    return this.today.toDate();
  }

  get exceptionAndLeadtimeDates(): Date[] {
    // Convert the string dates to Date objects (as required by PowerCalendar component)
    const exclusionDates = this.args.localSettingsCopy.exclusion_dates.map(
      (dateString) => dayjs(dateString).toDate(),
    );
    const leadtimeDates = this.leadtimeDates.map((dateString) =>
      dayjs(dateString).toDate(),
    );

    return [...exclusionDates, ...leadtimeDates];
  }

  get hasNextMonths(): boolean {
    // Number of months between the first current month shown and the last month possible to be shown
    const monthsDifference = this.lastAllowedMonth.diff(
      this.firstCurrentMonthShown,
      'month',
    );

    // If the number of months between the first current and last month shown is greater than the max number of months to be shown at once, then we have more months to show
    return monthsDifference > MAX_NUMBER_OF_MONTHS_SHOWN_AT_ONCE - 1;
  }

  get hasPreviousMonths(): boolean {
    // If the first current month shown is after today, then we have previous months to show
    return this.firstCurrentMonthShown.isAfter(this.today, 'month');
  }

  get lastAllowedMonth(): Dayjs {
    return this.today.add(MAX_ALLOWED_MONTHS_IN_FUTURE, 'month');
  }

  // Last day of the last month shown
  get lastAllowedMonthEndDate(): Dayjs {
    return this.lastAllowedMonth.endOf('month');
  }

  get leadtimeDates(): string[] {
    const dates = [];

    for (let i = 0; i < this.args.localSettingsCopy.min_push_lead_time; i++) {
      dates.push(this.today.add(i, 'days').format('YYYY-MM-DD'));
    }

    return dates;
  }

  get monthFormat(): string {
    return this.dates.partialDateFormat.YEAR_AND_MONTH_LONG;
  }

  get today(): Dayjs {
    return this.hotelAndMarketService.getLocalTime();
  }

  @action
  onNextButtonClicked(): void {
    if (this.args.isAutopilotEnabled && this.hasNextMonths) {
      this.firstCurrentMonthShown = this.firstCurrentMonthShown.add(
        MAX_NUMBER_OF_MONTHS_SHOWN_AT_ONCE,
        'month',
      );
    }
  }

  @action
  onPreviousButtonClicked(): void {
    if (this.args.isAutopilotEnabled && this.hasPreviousMonths) {
      this.firstCurrentMonthShown = this.firstCurrentMonthShown.subtract(
        MAX_NUMBER_OF_MONTHS_SHOWN_AT_ONCE,
        'month',
      );
    }
  }

  @action
  updateExceptionDatesOnSelection(selectedDays: { date: Date[] }): void {
    let lastSelectedDay = '';
    let isEnabled = false;

    // Convert the Date objects to string dates (as required by BE)
    const formattedSelectedDates = selectedDays.date
      .map((dateObject) => dayjs(dateObject).format('YYYY-MM-DD'))
      .filter((date) => {
        // Note: could be handled more performantly later if this ever becomes an issue
        // Note 2: we filter out the leadtime dates from the selected dates (since both will be returned in the selectedDays)
        // This is because technically we don't want to send over leadtime days as excluded days. In the payload we send on save we'll only send the actual exception dates.
        // Caveat: BE returns leadtime days as exception dates in the response on save though :trollface:
        // This was raised with Frederique (who worked on the BE), and this was described as a quirck/minibug which can be ignored for now.
        return !this.leadtimeDates.includes(date);
      });

    if (
      formattedSelectedDates.length >
      this.args.localSettingsCopy.exclusion_dates.length
    ) {
      // Assumes that the last day is always added at the end
      lastSelectedDay =
        formattedSelectedDates[formattedSelectedDates.length - 1] ?? '';
    } else {
      // Naive way to check for now which day exists in the current setting values, but not in the received selectedDays
      const enabledDay = this.args.localSettingsCopy.exclusion_dates.find(
        (day) => !formattedSelectedDates.includes(day),
      );

      if (enabledDay) {
        lastSelectedDay = enabledDay;
      }

      isEnabled = true;
    }

    trackAutopilotDayToggle(this.usageAnalytics, this.hotelAndMarketService, {
      date: lastSelectedDay,
      enabled: isEnabled,
      eventTrigger: 'autopilot-settings',
      product: 'pricing-manager',
    });

    // Update the local copy of the settings with the new exception dates
    const newSettingsObject = this.args.localSettingsCopy;
    newSettingsObject.exclusion_dates = formattedSelectedDates;
    this.args.updateLocalSettingsCopy(newSettingsObject);
  }

  <template>
    <div
      class="autopilot-calendar-months-container flex flex-row justify-between"
    >
      <div class="flex items-center">
        <PrismButton
          @buttonType="ghost"
          @icon="chevron-left"
          @isDisabled={{or
            (not @isAutopilotEnabled)
            (not this.hasPreviousMonths)
          }}
          @onClick={{this.onPreviousButtonClicked}}
        />
      </div>

      {{#each this.calendarMonthsShown as |month|}}
        <div class="autopilot-calendar-month">
          <PowerCalendarMultiple
            @center={{month}}
            @locale={{this.dates.locale}}
            @onSelect={{this.updateExceptionDatesOnSelection}}
            @selected={{this.exceptionAndLeadtimeDates}}
            class="demo-calendar-small"
            as |calendar|
          >
            <calendar.Nav>
              <p class="calendar-nav text-300--bold mb-200">
                {{powerCalendarFormatDate
                  calendar.center
                  this.monthFormat
                  locale=calendar.locale
                }}
              </p>
            </calendar.Nav>
            <calendar.Days
              @disabledDates={{this.leadtimeDates}}
              @minDate={{this.calendarsMinDate}}
              @showDaysAround={{false}}
              @startOfWeek={{this.dates.startOfWeek}}
              @weekdayFormat="min"
              as |day|
            >
              <div
                class="day-cell autopilot-settings-calendar-day flex justify-center
                  {{if
                    day.isDisabled
                    'autopilot-settings-calendar-day--disabled'
                  }}"
              >
                {{day.number}}
                {{#if day.isSelected}}
                  <div class="cross">
                    <PrismIcon @name="cross" />
                  </div>
                {{/if}}
              </div>
            </calendar.Days>
          </PowerCalendarMultiple>
        </div>
      {{/each}}

      <div class="flex items-center">
        <PrismButton
          @buttonType="ghost"
          @icon="chevron-right"
          @isDisabled={{or (not @isAutopilotEnabled) (not this.hasNextMonths)}}
          @onClick={{this.onNextButtonClicked}}
        />
      </div>
    </div>
  </template>
}
